import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import {
  ActionsModel,
  AspectRationFormatTree,
  AssetTagsService,
  AssetUIModel,
  AssetsGenerateUrlService,
  ClientEnvironment,
  CommonUtilityLib,
  CoreAssetsTagsService,
  DateNotExpiredPipe,
  ENVIRONMENT,
  FileObject,
  FormArrayMapping,
  FormControlModel,
  MultipleAgencyClientBrand,
  NestedTreeNode,
  NotificationService,
  PROMPTS,
  StepperGroupModel,
  StepperModel,
  StorageUtilityLib,
  USER_SESSION_TOKEN,
  UserSession,
  addTagToArray,
  anyAsset,
} from '@thehubz/client/core';
import {
  Asset,
  AssetDetails,
  AssetSearchResult,
  AssetsApiService,
  Collection,
  CollectionResultResponse,
  CollectionsApiService,
  CompanyBaseDataWithClients,
  Tags,
  TagsApiService,
  User,
  UserSearchResult,
  UsersApiService,
} from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata } from '@thehubz/shared/model';
import { Subject, Subscription, debounce, filter, map, takeUntil, tap, timer } from 'rxjs';
import { MatSelectSearchComponent } from '../mat-select-search/mat-select-search.component';
import { MatTreeComponent } from '../mat-tree/mat-tree.component';
import { StepperDialogFormService } from './services/stepper-dialog-form.service';
import { StepperDialogArrayUtilService } from './stepper-dialog-array-util.service';
import { StepperDialogService } from './stepper-dialog.service';

@Component({
  selector: 'sp-stepper-dialog',
  templateUrl: './stepper-dialog.component.html',
  styleUrls: ['./stepper-dialog.component.scss'],
})
export class StepperDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSelectSearchComponent) matSelectSearchComp?: MatSelectSearchComponent;
  @ViewChild(MatTreeComponent) matTree!: MatTreeComponent;

  readonly PROMPTS = PROMPTS;
  readonly brandingKey: CustomClients;
  readonly CustomClients = CustomClients;

  stepperHeader!: string;
  errorFlag = false;
  disableButtons = true;
  isMatTreeSelected = false;
  uploadInProgress = false;
  fileUploadRequired = false;
  initialStepper!: StepperModel;
  stepperFormGroups!: FormGroup;
  assetDetails: AssetDetails[] = [];
  matTreeRawData: AspectRationFormatTree[] = [];
  users: User[] = [];
  assetUrlService: AnyType;
  // set mat tree checkbox to true on load
  matTreeCheckbox = false;
  formControlValidState = false;
  currentUserID!: string;
  brandMissing = '';
  clientListMapping: FormArrayMapping = {};
  agencyName: FormArrayMapping = {};
  agencyClientBrandData: MultipleAgencyClientBrand = {};
  agencyDetails!: { key: string; company: string; name: string };
  selectedAgencyList: string[] = [];
  disableACBButton!: boolean;
  collectionAssetsInfo?: AnyType;
  tags: string[] = [];
  // list of user email from input mat-chip
  listUsers: string[] = [];
  /* File uploads and assets vars */
  files: FileObject[] = [];
  progress = 0;
  currentUploadFileName = '';
  collectionsList: Collection[] = [];
  imgData: AnyType = {};
  isEditorError = false;
  formControlSub?: Subscription;
  tagSub?: Subscription;
  /** Auto complete tag data */
  autoCompTags: Tags[] = [];
  allTagsListOption: Tags[] = [];
  AnyAsset = anyAsset;

  readonly requiredText = $localize`is required`;
  readonly tagPlaceHolderText = $localize`Search tags...`;
  readonly textChipInputTooltip = $localize`:tooltip:Press \'Enter\' or \'Comma\' to add tags`;
  readonly textCopySharedLinkButton = $localize`Copy link`;
  readonly textCopySharedLinkSuccess = $localize`Sharing link copied`;

  /**
   * Observable to emit when the component is destroyed.
   */
  private _destroyed$ = new Subject<void>();

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    @Inject(MAT_DIALOG_DATA)
    public stepperModel: {
      stepperData: StepperModel;
      additionalData?: AnyType[];
      collectionData?: Collection;
      callFrom?: string;
      editable?: boolean;
    },
    public dialogRef: MatDialogRef<StepperDialogComponent>,
    private el: ElementRef,
    private dialog: MatDialog,
    private _formBuilder: FormBuilder,
    private tagsApiService: TagsApiService,
    private usersService: UsersApiService,
    private assetApiService: AssetsApiService,
    private assetTagsService: AssetTagsService,
    private storageUtility: StorageUtilityLib,
    private commonUtilityLib: CommonUtilityLib,
    private coreAssetsTagsService: CoreAssetsTagsService,
    private notificationsSer: NotificationService,
    private collectionAPIService: CollectionsApiService,
    private formService: StepperDialogFormService,
    private assetGenerateUrlService: AssetsGenerateUrlService,
    private stepperDialogService: StepperDialogService,
    private arrayUtilService: StepperDialogArrayUtilService,
    private dateNotExpiredPipe: DateNotExpiredPipe,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    this.brandingKey = this.env.brandingKey;
    this.currentUserID = this.storageUtility.getLocalStorage<UserSession>(this.userSessionToken).userData._id;
  }

  ngOnInit(): void {
    this.stepperFormGroups = this._formBuilder.group({});
    this.initialStepper = JSON.parse(JSON.stringify(this.stepperModel.stepperData));
    this.assetUrlService = this.dialogRef.componentInstance.assetUrlService;
    const stepperInfo = Object.assign({}, ...this.initialStepper.stepper.filter((step: StepperGroupModel) => step.selected));
    this.stepperHeader = !stepperInfo.hideHeader ? stepperInfo.stepperName : '';
    this.stepperDialogService.resetAllSteps(this.initialStepper, 0);
    if (this.brandingKey === CustomClients.LufthansaMedialounge) {
      if (this.stepperModel.callFrom === 'editCollection') {
        this.removeDescriptionValidator();
        if (this.stepperModel.collectionData) {
          this.collectionAssetsInfo = this.assetGenerateUrlService.generatePreview(this.stepperModel.collectionData.assets ?? []);
        }
        if (this.collectionAssetsInfo.length > 0) {
          const coverField = this.initialStepper.stepper[0].formFields?.filter((formField) => formField.formFieldName == 'cover');
          if (coverField) {
            coverField[0].options = this.collectionAssetsInfo
              .filter((asset: AssetUIModel) => asset.imageUrl && asset.assetType !== Asset.AssetTypeEnum.HtmlBanner)
              .map((asset: AssetUIModel) => {
                return { _id: asset._id, name: asset.name, imageUrl: asset.imageUrl };
              });
            const coverAsset = this.stepperModel?.collectionData?.coverAssetID;
            const coverAssetID = coverAsset && typeof coverAsset === 'object' ? (coverAsset as AssetUIModel)._id : coverAsset;
            coverField[0].value = this.collectionAssetsInfo
              .filter((asset: AssetUIModel) => asset._id === coverAssetID)
              .map((asset: AssetUIModel) => {
                return { _id: asset._id, name: asset.name, imageUrl: asset.imageUrl };
              })[0];
          }
        }
      }
      if (this.initialStepper.stepID === 'shareModel') {
        this.removeMaxDateFromExpiryDate();
      }
    }
    this.initStepperFormGroups();
    this.fetchUserOnEmail();
    if (this.stepperModel.stepperData.stepID === 'newVersion' && this.brandingKey !== CustomClients.VKB) {
      this.tagSub = this.assetTagsService
        .fetchTagsForAutoComp(this.stepperFormGroups.controls['metadata'].get(AssetMetadata.General.TAGS))
        ?.pipe(tap((data) => (this.autoCompTags = data)))
        .subscribe();
    }
    if (this.brandingKey === CustomClients.VKB) {
      this.tagsApiService.tagsControllerFindAll().subscribe((data) => {
        if (data) this.allTagsListOption = data;
      });
    }

    if (this.stepperModel.editable) {
      switch (this.stepperModel.callFrom) {
        case 'publishCollection': {
          this.patchCollectionStepper();
          break;
        }
      }
    }
  }

  ngAfterViewInit(): void {
    timer(0).subscribe(() => {
      this.callOriginalFileSelection();
    });
  }

  callOriginalFileSelection(selectedOptions?: AnyType[]) {
    const data = this.stepperModel.stepperData?.stepper[0].formFields;
    const treeHierarchyIndex = data?.findIndex((formField: AnyType) => formField.fieldType == 'treeHierarchy');
    if (treeHierarchyIndex != -1 && treeHierarchyIndex != undefined && data) {
      const selectedOptionsFormControl = this.stepperFormGroups.controls['selectFiles'].get('selectedOptions') as FormControl;
      const selectedOptionsIndex = data?.findIndex((formField: AnyType) => formField.formFieldName == 'selectedOptions');
      if (selectedOptionsFormControl && data && selectedOptionsIndex != -1 && selectedOptionsIndex != undefined && data[selectedOptionsIndex].options) {
        const defaultValue = data[selectedOptionsIndex].options;
        if (defaultValue) {
          selectedOptionsFormControl.setValue([defaultValue[0]]);
        }
      }
      this.checkOriginalFiles(data[treeHierarchyIndex].value, selectedOptions);
    }
  }

  onSelectedChange(event: AnyType, formControlName: string) {
    if (formControlName === 'selectedOptions') {
      if (event.value.length > 0) {
        this.callOriginalFileSelection(event.value);
      } else {
        this.matTree.setAll(false);
      }
    }
  }

  checkOriginalFiles(dataValue: AnyType, selectedOptions?: AnyType[]) {
    this.matTree.setAll(false);
    if (this.matTree.treeControl) {
      for (let i = 0; i < dataValue.length; i++) {
        let item;
        if (selectedOptions && selectedOptions.length > 0) {
          selectedOptions.forEach((option: AnyType) => {
            item = this.matTree.treeControl.dataNodes?.[i]?.children?.[0]?.children?.find((child: AnyType) => child.fileNameRef === option._id);
            this.updateSelectedOptions(dataValue, item, i);
          });
        } else {
          item = this.matTree.treeControl.dataNodes?.[i]?.children?.[0]?.children?.[0];
          this.updateSelectedOptions(dataValue, item, i);
        }
      }
    }
  }

  updateSelectedOptions(dataValue: AnyType, item: AnyType, i: number) {
    const expiryDate = dataValue[i].expiryDate;
    if (item) {
      if (this.dateNotExpiredPipe.transform(expiryDate)) {
        this.matTree.itemToggle(true, item);
        if (item.parent?.parent) {
          this.matTree.treeControl.expandDescendants(item.parent?.parent);
        }
      } else {
        this.matTree.itemToggle(false, item);
        if (item.parent?.parent) {
          this.matTree.treeControl.collapse(item.parent?.parent);
        }
      }
    }
  }

  patchCollectionStepper() {
    if (this.stepperModel.additionalData?.length) {
      // get form fields from stepper
      const formFields = this.initialStepper.stepper.map((step) => step.formFields).flat(1) as NonNullable<FormControlModel[]>;
      // get form field group from stepper
      const formFieldGroup: FormControlModel[] = formFields
        .filter((form) => form.formFieldGroup)
        .map((form) => form.formFieldGroup)
        .flat(2) as NonNullable<FormControlModel[]>;
      // agency index in form field group
      const agencyIndex = formFieldGroup?.findIndex((obj: AnyType) => obj.formFieldName == 'agency');
      // agency list from form field group option
      const agencyOptions = agencyIndex !== -1 ? formFieldGroup[agencyIndex]['options'] : [];

      // removing 1st formArray formControl as it is created by default by createStepperFormGroups method
      (this.stepperFormGroups.controls['information'].get('publishCollection') as UntypedFormArray).removeAt(0);
      this.stepperModel.additionalData.forEach((data, i) => {
        this.addRow('information', 'publishCollection', formFieldGroup);

        // get the original agency list
        const origClientList = agencyOptions?.find((agency) => agency._id === data._id);
        // call agencyValueChange for selecting the client and brands for mat tree
        switch (this.stepperModel.callFrom) {
          case 'publishCollection': {
            this.agencyValueChange({ source: {} as AnyType, value: data }, 'agency', formFields[0], i, ...this.initialStepper.stepper, origClientList.clients);
            break;
          }
          case 'editCollection': {
            this.agencyValueChange(
              { source: {} as AnyType, value: data },
              'agency',
              formFields[5],
              i,
              { ...this.initialStepper.stepper[1] },
              origClientList.clients,
            );

            // singe agency patch fix
            // The timeout ensures that this call starts after ngonchanges from mat-select-search as finished
            if (this.stepperModel.additionalData?.length === 1) {
              timer(100).subscribe(() => {
                this.agencyValueChange(
                  { source: {} as AnyType, value: data },
                  'agency',
                  formFields[5],
                  i,
                  { ...this.initialStepper.stepper[1] },
                  origClientList.clients,
                );
              });
            }
            break;
          }
        }
        // patch the agency value
        (this.stepperFormGroups.controls['information'].get('publishCollection') as UntypedFormArray).at(i).get('agency')?.patchValue(data);
      });
    }
  }

  initStepperFormGroups() {
    this.stepperFormGroups = new FormGroup({});
    if (this.initialStepper) {
      if (this.initialStepper.stepID === 'createNewCollection') {
        this.fetchAllCollections();
      }
      this.stepperFormGroups = this.createStepperFormGroups(this.initialStepper['stepper']);
      // using setTimeout because stepperFormGroups doesn't initialize selectType property
      setTimeout(() => {
        if (this.stepperFormGroups.controls['collectionMetadata']?.get('keywords')) {
          if (this.brandingKey !== CustomClients.VKB) {
            this.tagSub = this.assetTagsService
              .fetchTagsForAutoComp(this.stepperFormGroups.controls['collectionMetadata'].get('keywords'))
              ?.pipe(tap((data) => (this.autoCompTags = data)))
              .subscribe();
          }

          if (this.brandingKey === CustomClients.VKB) {
            this.tagsApiService.tagsControllerFindAll().subscribe((data) => {
              if (data) this.allTagsListOption = data;
            });
          }
        }
      });
    }
  }

  /**
   * Create the form groups for each step
   * @param steps
   * @returns
   */
  createStepperFormGroups(steps: StepperGroupModel[]) {
    const stepperFormGroups = new FormGroup({});
    steps.forEach((step) => {
      const [formGroup, tags] = this.formService.getFormGroupFor(step);
      // assuming only 1 input for tags/keywords in the whole stepper dialog
      this.tags = tags.length ? tags : this.tags;

      stepperFormGroups.addControl(step.stepperGroupName, formGroup);
    });
    return stepperFormGroups;
  }

  /**
   * This function is used for formArray. Mainly for the milestone. It will return the form control based on field name
   * @param fieldArrayName
   * @param formGroupName
   * @returns
   */
  getControls(fieldArrayName: AnyType, formGroupName: AnyType) {
    if (this.stepperFormGroups.controls[formGroupName]) {
      const formGroup = this.stepperFormGroups.controls[formGroupName] as UntypedFormGroup;
      const formArray = formGroup.controls[fieldArrayName] as UntypedFormArray;
      return formArray.controls;
    }
    return [];
  }

  createFilterObj(nameObj: AnyType) {
    const filterObject: AnyType = {};
    filterObject['filters'] = { ...nameObj };
    filterObject['projection'] = { name: 1 };
    return filterObject;
  }

  onValueChange(event: MatSelectChange, formFieldName: string, formField: FormControlModel, formGroupField?: AnyType) {
    let collectiveStepper = [];
    if (this.stepperModel.callFrom === 'editCollection') {
      collectiveStepper = JSON.parse(JSON.stringify(this.initialStepper['stepper']));
    } else {
      collectiveStepper = JSON.parse(JSON.stringify(this.initialStepper['collectiveStepper']));
    }

    if (formFieldName === 'selectType' && collectiveStepper.length > 0) {
      this.disableButtons = false;
      const selectedStepper = Object.assign({}, ...collectiveStepper.filter((element: AnyType) => element.stepID === event.value.id));
      this.initialStepper['stepper'] = [...this.stepperModel.stepperData['stepper'], ...selectedStepper.stepper];
      if (this.brandingKey === CustomClients.LufthansaMedialounge) {
        this.removeDescriptionValidator();
      }

      this.stepperFormGroups.controls[formGroupField.stepperGroupName]
        .get(formFieldName)
        ?.valueChanges.pipe(takeUntil(this._destroyed$))
        .subscribe((data) => {
          if (data) {
            this.initStepperFormGroups();
            this.stepperFormGroups.controls[formGroupField.stepperGroupName].get(formFieldName)?.patchValue(data);
            const actionObj = formGroupField['actions'].find((actionList: AnyType) => actionList.actionType === 'next');
            this.nextGoToStep(actionObj);
          }
        });
    } else if (formField.withImg) {
      formField.imgPreviewUrl = event.value.previewUrl;
      this.imgData = event.value;
    }
  }

  removeMaxDateFromExpiryDate() {
    if (this.initialStepper && this.initialStepper.stepper && this.initialStepper.stepper.length > 0) {
      const shareInfoStepperIndex: number = this.initialStepper.stepper.findIndex((obj: AnyType) => obj.stepperGroupName === 'shareInfo');
      if (shareInfoStepperIndex !== -1) {
        const expiryDateIndex = this.initialStepper.stepper[shareInfoStepperIndex].formFields?.findIndex((obj: AnyType) => obj.formFieldName == 'expiryDate');
        if (expiryDateIndex != -1 && expiryDateIndex != undefined) {
          const tempStepperObject = this.initialStepper.stepper[shareInfoStepperIndex].formFields;
          if (tempStepperObject && tempStepperObject[expiryDateIndex]) {
            tempStepperObject[expiryDateIndex].maxDate = undefined;
          }
        }
      }
    }
  }

  removeDescriptionValidator() {
    if (this.initialStepper && this.initialStepper.stepper && this.initialStepper.stepper.length > 0) {
      const collectionMetadataStepperIndex: number = this.initialStepper.stepper.findIndex((obj: AnyType) => obj.stepperGroupName === 'collectionMetadata');
      if (collectionMetadataStepperIndex !== -1) {
        const descriptionIndex = this.initialStepper.stepper[collectionMetadataStepperIndex].formFields?.findIndex(
          (obj: AnyType) => obj.formFieldName == 'description',
        );
        if (descriptionIndex != -1 && descriptionIndex != undefined) {
          const tempStepperObject = this.initialStepper.stepper[collectionMetadataStepperIndex].formFields;
          if (tempStepperObject && tempStepperObject[descriptionIndex]) {
            delete tempStepperObject[descriptionIndex].validators;
          }
        }
      }
    }
  }

  agencyValueChange(
    event: MatSelectChange,
    formFieldName: string,
    formField: FormControlModel,
    index: number,
    formGroupField?: AnyType,
    preSelectedClients?: AnyType,
  ) {
    if (formFieldName === 'agency') {
      // transform agency's client model to mat tree data model
      const agencyClient: NestedTreeNode = Object.assign(
        {},
        ...this.stepperDialogService.transformClients(event.value.clients, preSelectedClients, this.stepperModel.editable),
      );
      // client list mapping for mat tree
      this.clientListMapping[index] = agencyClient;
      // get form array controls
      const formArrayControls = (this.stepperFormGroups.controls[formGroupField.stepperGroupName].get(formField.formFieldName) as UntypedFormArray).at(index);
      // populate the agency
      formArrayControls
        .get(formFieldName)
        ?.valueChanges.pipe(takeUntil(this._destroyed$))
        .subscribe((data) => {
          if (data) {
            this.agencyDetails = {
              key: data._id,
              company: data._id,
              name: data.name,
            };
            // if open in editable mode (already published)
            if (this.stepperModel.editable && preSelectedClients?.length) {
              // call selectedClientBrand to add the selected agency client brand in this.agencyClientBrandData
              this.selectedClientBrand([agencyClient], index);
              timer(0).subscribe(() => {
                this.setArrayFieldValidation(index, 'clientNBrand');
              });
            }
            // update the selected agency list
            this.selectedAgencyList[index] = event.value.name;
            if (this.selectedAgencyList.length === (formField.formFieldGroup && formField.formFieldGroup[0]['options']?.length)) {
              this.disableACBButton = true;
            } else {
              this.disableACBButton = false;
            }
            // updated selected property to disable the option list once selected
            if (formField.formFieldGroup && formField.formFieldGroup[0]['options']?.length !== 1) {
              this.changeAgencyList(formField.formFieldGroup || []);
            }
          }
        });
    }
  }

  /**
   * Change selected property value
   * @param formFieldGroup
   */
  changeAgencyList(formFieldGroup: FormControlModel[]) {
    const agencyIndex = formFieldGroup?.findIndex((obj: AnyType) => obj.formFieldName == 'agency');
    if (agencyIndex !== -1) {
      const selectionChanged = formFieldGroup[agencyIndex]['options']?.map((option) => {
        const selected = this.selectedAgencyList.includes(option.name);
        return { ...option, selected: selected };
      });
      formFieldGroup[agencyIndex]['options'] = selectionChanged;
    }
  }

  /**
   * Trigger when mat tree value change
   * Trigger when you editable true
   * Store the selected agency and client brand details
   * @param event
   * @param index
   */
  selectedClientBrand(event: NestedTreeNode[], index: number) {
    const clientsBrandsData = event[0].children
      ?.map((client: NestedTreeNode) => {
        if (client.children && client.children[0].children) {
          const brandArr = client.children[0].children
            .filter((brand) => brand.selected)
            .map((brand) => ({ name: brand.name || '', key: brand.id || '', brand: brand.id || '' }));
          return { name: client.name || '', key: client.id || '', client: client.id || '', brands: brandArr };
        }
        return { name: client.name || '', key: client.id || '', client: client.id || '', brands: [] };
      })
      .filter((client) => client.brands.length);
    // Comma separated agency list to show on mat select
    this.agencyName[index] = clientsBrandsData?.map((data) => data.name).join(', ') || '';

    // Agency, clients and brands data model
    this.agencyClientBrandData[index] = {
      ...this.agencyDetails,
      clients: clientsBrandsData,
    };
  }

  setArrayFieldValidation(index: number, fieldName: string) {
    if (!this.agencyClientBrandData[index].clients?.length) {
      (this.stepperFormGroups.controls['information'].get('publishCollection') as UntypedFormArray).at(index).get(fieldName)?.setErrors({ required: true });
    } else {
      (this.stepperFormGroups.controls['information'].get('publishCollection') as UntypedFormArray).at(index).get(fieldName)?.setErrors(null);
    }
  }

  isArrayFieldValid(index: number, fieldName: string): boolean {
    const errors = (this.stepperFormGroups.controls['information'].get('publishCollection') as UntypedFormArray).at(index).get(fieldName)?.errors;
    return errors ? false : true;
  }

  onInput(event: AnyType, formField: FormControlModel, i: number, groupName: string) {
    const input = event.target.value;
    // Check if input is in numeric form (e.g., 24082024)
    if (/^\d{8}$/.test(input)) {
      const day = input.substring(0, 2);
      const month = input.substring(2, 4);
      const year = input.substring(4, 8);

      // Format the date as DD/MM/YYYY
      const formattedDate = `${month}/${day}/${year}`;

      // Update the dateValue bound to the input
      const newDate = new Date(formattedDate);
      event.value = newDate;
      this.stepperFormGroups.controls[groupName].get(formField['formFieldName'])?.patchValue(newDate);
    }
  }

  compareMatSelect(list: AnyType, selected: AnyType): boolean {
    return list && selected ? list._id === selected._id && list.id === selected.id : list === selected;
  }

  closeModal() {
    if (!this.stepperFormGroups.pristine) {
      this.stepperDialogService.confirmDialogClose(this.dialog, this.dialogRef);
    } else {
      this.dialogRef.close();
    }
  }

  /**
   * Add controls to form array
   * @param formFieldName
   * @param stepperGroupName
   * @param formFieldGroup
   */
  addRow(stepperGroupName: string, formFieldName: string, formFieldGroup: AnyType) {
    const agencyClientBrand = this.stepperFormGroups.controls[stepperGroupName].get(formFieldName) as UntypedFormArray;
    agencyClientBrand.push(this.formService.buildFormGroup(formFieldGroup));
  }

  /**
   * Remove controls from form array
   * @param i
   * @param formFieldName
   * @param stepperGroupName
   */
  removeRow(i: number, stepperGroupName: string, formFieldName: string, formField: FormControlModel) {
    const agencyClientBrand = this.stepperFormGroups.controls[stepperGroupName].get(formFieldName) as UntypedFormArray;
    const agencyIndex = this.selectedAgencyList.indexOf(agencyClientBrand.value[i].agency.name);
    if (agencyIndex !== -1) this.selectedAgencyList.splice(agencyIndex, 1);
    if (this.selectedAgencyList.length !== (formField.formFieldGroup && formField.formFieldGroup[0]['options']?.length)) {
      this.disableACBButton = false;
    }
    this.changeAgencyList(formField.formFieldGroup || []);
    if (agencyClientBrand.length > 1) {
      agencyClientBrand.removeAt(i);
      delete this.agencyClientBrandData[i];
      delete this.clientListMapping[i];
      delete this.agencyName[i];
    } else {
      this.agencyClientBrandData[i] = {} as CompanyBaseDataWithClients;
      this.clientListMapping[i] = {};
      this.agencyName[i] = '' as string;
      this.matSelectSearchComp?.clearLabel();
      this.setArrayFieldValidation(i, 'clientNBrand');
    }
  }

  showSelectedStepper(stepDetails: StepperGroupModel, index: number) {
    if (index !== 0) {
      const groupName = this.initialStepper.stepper[index - 1].stepperGroupName;
      const control = this.stepperFormGroups.controls[groupName];
      if (control.invalid) {
        this.formInvalidState(control);
        return;
      }
    }
    this.stepperHeader = !stepDetails.hideHeader ? stepDetails.stepperName : '';
    this.stepperDialogService.resetAllSteps(this.initialStepper, index);
    if (this.stepperModel.callFrom === 'editCollection' && !this.disableACBButton) {
      this.patchCollectionStepper();
    }
  }

  private formInvalidState(control: AbstractControl<AnyType, AnyType>) {
    control.markAllAsTouched();
    control.markAsDirty();
    this.fileUploadRequired = true;
    this.isMatTreeSelected = true;
    this.formControlValidState = true;
  }

  /**
   * Move to next step
   * @param action
   * @param control
   * @returns
   */
  nextGoToStep(action: ActionsModel, control?: AbstractControl<AnyType, AnyType>) {
    const firstRequiredControl = this.commonUtilityLib.getRequiredControls(control as UntypedFormGroup);
    if (firstRequiredControl) {
      const invalidFormField = this.el.nativeElement.querySelector('[id="stepper-dialog_' + firstRequiredControl + '"]');
      if (invalidFormField) invalidFormField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (control?.invalid) {
      this.formInvalidState(control);
      return;
    }
    this.formControlValidState = false;
    this.imgData = {};
    switch (action?.actionType) {
      case 'createVersion': {
        this.dialogRef.disableClose = true;
        const formValueObject = this.stepperDialogService.getFormDirtyValues(this.stepperFormGroups);
        this.dialogRef.close({ data: formValueObject });
        break;
      }
      case 'createNewCollection': {
        const tempData = this.stepperFormGroups.controls['collectionMetadata'].value;
        if (tempData.createNewCollection) {
          this.checkExisting();
        } else {
          this.dialogRef.disableClose = true;
          this.dialogRef.close({ data: this.stepperFormGroups.value });
        }
        break;
      }
      case 'shareAsset': {
        this.assetDetailStructure(action);
        break;
      }
      case 'taskSharing': {
        this.taskSharingDetailStructure(action);
        break;
      }
      case 'userName': {
        this.dialogRef.close({ data: this.stepperFormGroups.value });
        break;
      }
      case 'publishCollection': {
        this.dialogRef.close({ ...this.agencyClientBrandData });
        break;
      }

      case 'updateCollection': {
        const formValueObject = this.stepperDialogService.getFormDirtyValues(this.stepperFormGroups.controls['collectionMetadata']);
        if (!this.commonUtilityLib.isEmptyObject(this.agencyClientBrandData)) {
          formValueObject.publish = this.agencyClientBrandData;
        }
        this.dialogRef.close({ ...formValueObject });
        break;
      }
      default: {
        const stepDetails = this.initialStepper.stepper[(action?.goToStep || 0) - 1];
        this.showSelectedStepper(stepDetails, (action?.goToStep || 0) - 1);
        break;
      }
    }
  }

  /**
   * check for field validation
   * @param formField
   * @returns
   */
  getFieldValidation(formField: AbstractControl<AnyType, AnyType> | null): boolean {
    return !!(formField?.errors?.['required'] && formField?.touched);
  }

  /**
   * Add tags
   * @param event
   * @param stepperGroupName
   * @param fieldName
   * @returns
   */
  async add(event: AnyType, stepperGroupName: string, fieldName: string) {
    if (!event || this.tags.includes(event)) return;
    if (this.env.feature?.tagGroups === true && event._id) {
      await this.coreAssetsTagsService.handleTagAddition(event, this.tags);
    } else {
      addTagToArray(event, this.tags);
    }

    this.updateTagsArray(this.tags, stepperGroupName, fieldName);
  }

  /**
   * Update tags array in the form group and service
   * @param tagsArray
   * @param stepperGroupName
   * @param fieldName
   */
  private updateTagsArray(tagsArray: string[], stepperGroupName: string, fieldName: string) {
    const formGroup = this.stepperFormGroups.controls[stepperGroupName];

    this.coreAssetsTagsService.updateTagsArray(tagsArray, formGroup, fieldName);
  }

  tagOptionChanged(evt: AnyType, stepperGroupName: string, fieldName: string) {
    if (evt.fieldSelected) {
      this.add(evt.name, stepperGroupName, fieldName);
    } else {
      this.removeTag(evt.name, stepperGroupName, fieldName);
    }
  }

  removeTagOnChange(evt: AnyType, stepperGroupName: string, fieldName: string) {
    this.removeTag(evt.list.name || evt.list, stepperGroupName, fieldName);
  }

  /**
   * Remove tags
   * @param tag
   * @param stepperGroupName
   * @param fieldName
   */
  removeTag(tag: string, stepperGroupName: string, fieldName: string): void {
    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
      this.tags.sort((a, b) => a.localeCompare(b));
      this.stepperFormGroups.controls[stepperGroupName].patchValue({
        [fieldName]: this.tags,
      });
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsDirty();
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsTouched();
    }
  }

  /**
   * Remove chip from multi select
   * @param value
   */
  removeMultiSelectChip(value: AnyType, stepperGroupName: string, fieldName: string) {
    const chip = this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.value;
    const modifiedChip = this.commonUtilityLib.removeObjectFromArray(chip, value.list.id ?? value.list._id);
    if (fieldName === 'selectedOptions') {
      const event = { value: modifiedChip };
      this.onSelectedChange(event, fieldName);
    }
    this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.setValue(modifiedChip);
    this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsDirty();
  }

  /**
   * mat-checkbox capture change event
   * @param event
   * @param stepperGroup
   * @param formFieldName
   */
  checkboxChanged(event: MatCheckboxChange, stepperGroup: string, formField: FormControlModel) {
    this.stepperFormGroups.controls[stepperGroup].patchValue({
      [formField.formFieldName]: event.checked,
    });

    if (formField.formFieldName === 'publicLink') {
      this.sharingPublicLinkSelection(event, stepperGroup);
    }
  }

  /**
   * If mat tree status changes (checked or unchecked)
   * @param event
   * @param stepperGroupName
   * @param fieldName
   */
  changeStatus(event: boolean, stepperGroupName: string, fieldName: string) {
    // Wait fot the next event loop until all checkbox states have been updated. Necessary when toggling the "Select all" root checkbox.
    setTimeout(() => {
      const matTreeField = this.stepperFormGroups.controls[stepperGroupName].get(fieldName);
      const isNotEmpty = matTreeField?.value.some((entry: AnyType) => entry.selected || entry.indeterminate);
      if (isNotEmpty) {
        matTreeField?.setErrors(null);
        this.disableButtons = false;
      } else {
        matTreeField?.setErrors({ required: true });
        this.disableButtons = true;
      }
    });
  }

  /**
   * MaT tree returns checked nodes
   * @param event
   */
  selectedFormatList(event: AspectRationFormatTree[]) {
    this.matTreeRawData = event;
    if (this.matTreeRawData.length) this.isMatTreeSelected = false;
  }

  /**
   * Convert mat tree data (parent child structure) into assetDetails/aspectRatios structure shareAsset
   * Close the dialog
   * @param action
   */
  assetDetailStructure(action: ActionsModel) {
    this.assetDetails = [];
    const indSelect = this.matTreeRawData.filter((ele) => ele.indeterminate);
    const selectedFlatArray = this.arrayUtilService.flatNestedArray(indSelect).filter((ele) => ele.selected && ele?.parent?.parent);
    const assetIDKey = this.arrayUtilService.transform2LvlArray(selectedFlatArray);
    Object.entries(assetIDKey).forEach(([key, value]) => {
      let assetParentParentDetails = {};
      value.forEach((element) => {
        const elem = element.parent?.parent;
        assetParentParentDetails = { name: elem?.name, version: elem?.version };
      });
      const assetObj: AnyType = { assetId: key, ...assetParentParentDetails, aspectRatios: [] };
      const aspectRatioArr = this.arrayUtilService.transform1LvlArray(value);

      Object.entries(aspectRatioArr).forEach(([key, value]) => {
        // get aspect parent details
        const aspectParentDetails = this.stepperDialogService.getAspectParentDetails(value);
        const aspectRatioObj: AnyType = { id: key, ...aspectParentDetails, formats: [] };

        // get aspect formats
        aspectRatioObj.formats = this.stepperDialogService.getFormats(value);

        assetObj.aspectRatios.push(aspectRatioObj);
      });
      this.assetDetails.push(assetObj);
    });
    this.stepperFormGroups.controls['selectFiles'].patchValue({
      ['assetFileFormatList']: this.assetDetails,
    });
    this.stepperFormGroups.controls['shareInfo'].get('email')?.patchValue(this.listUsers);
    action.disable = true;
    this.dialogRef.disableClose = true;
    this.dialogRef.close({ data: this.stepperFormGroups.value });
  }

  taskSharingDetailStructure(action: ActionsModel) {
    this.stepperFormGroups.controls['shareInfo'].get('email')?.patchValue(this.listUsers);
    action.disable = true;
    this.dialogRef.disableClose = true;
    this.dialogRef.close({ data: this.stepperFormGroups.value });
  }

  /**
   * Dynamically fetching the users every 500 millisec on user keyup in email field for matching users based on email
   */
  fetchUserOnEmail() {
    let valueChangeStoredUser: User[] = [];
    this.formControlSub = this.stepperFormGroups.controls['shareInfo']
      ?.get('email')
      ?.valueChanges.pipe(debounce(() => timer(500)))
      .subscribe((value: string) => {
        const checkUserExist = valueChangeStoredUser.some((t) => t.email === value);
        if (value && typeof value !== 'object' && !checkUserExist) {
          this.usersService.usersControllerFindAllByEmail(value).subscribe((user: UserSearchResult) => {
            this.users = user.result;
            valueChangeStoredUser = this.users;
          });
        }
      });
  }

  /**
   * Set values in listUsers from sp-user-email-input-autocomplete component
   * @param user
   */
  userSelected(user: string[]) {
    this.listUsers = user;
  }

  /*-------------------- File upload and assets creation funcs ---------------------*/

  updatedFilesList(event: { files: FileObject[] | AnyType[]; removedAsset: AnyType }) {
    this.files = event.files;
    if (this.files.length === 0) {
      this.disableButtons = true;
      this.fileUploadRequired = true;
    }
    this.stepperFormGroups.controls['uploadAssets'].patchValue({
      ['assetData']: this.files,
    });
  }

  selectedFiles(event: FileObject[]) {
    this.files = event;
    this.disableButtons = false;
    this.fileUploadRequired = false;
    this.stepperFormGroups.controls['uploadAssets'].patchValue({
      ['assetData']: this.files,
    });
    this.stepperFormGroups.controls['uploadAssets'].get('assetData')?.markAsDirty();
  }

  checkExisting() {
    const tempData = this.stepperFormGroups.controls['collectionMetadata'].value;

    this.collectionAPIService
      .collectionsControllerFindAll('')
      .pipe(map((collectionsResult: CollectionResultResponse) => collectionsResult.result))
      .subscribe((collectionList: Collection[]) => {
        const tempIdx: number = collectionList.findIndex(
          (collection: Collection) => collection.name.toLowerCase().trim() == tempData['add2ExistingCollection'].name.toLowerCase().trim(),
        );
        if (tempIdx >= 0) {
          this.notificationsSer.addNotification({
            type: 'failure',
            message: $localize`Collection already available with same cover and name. So, please try with another name`,
          });
        } else {
          this.dialogRef.disableClose = true;
          this.dialogRef.close({ data: this.stepperFormGroups.value });
        }
      });
  }

  /**
   * Fetch all the collections
   */
  fetchAllCollections() {
    const filterObject = this.stepperDialogService.getFilter(this.currentUserID);

    // call the api
    this.collectionAPIService
      .collectionsControllerFindAll(JSON.stringify(filterObject))
      .pipe(
        map((collectionsResult: CollectionResultResponse) => collectionsResult.result),
        filter((collections: Collection[]) => collections && !!collections.length),
      )
      .subscribe(async (collectionList: Collection[]) => {
        this.collectionsList = collectionList;
        this._getAssets();
      });
  }

  editorInput(event?: AnyType) {
    this.isEditorError = (event ? event.srcElement : document.getElementById('textareaEditor'))?.innerText.toString().trim().length;
  }

  ngOnDestroy(): void {
    if (this.formControlSub) this.formControlSub.unsubscribe();
    if (this.tagSub) this.tagSub.unsubscribe();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Get all assets
   */
  private _getAssets() {
    // get the asset ids
    const assetsIds = this.stepperDialogService.getAssetIDsFromCollections(this.collectionsList);
    if (assetsIds.length) {
      // call the api
      this.assetApiService
        .assetsControllerFindMany({ ids: assetsIds })
        .pipe(map((searchResult: AssetSearchResult) => searchResult.result || []))
        .subscribe(async (assets: AssetUIModel[]) => {
          // generate preview urls
          assets = await this.assetUrlService.generatePreview(assets);

          // update the collections
          this.stepperDialogService.updateCollectionsAssets(assets, this.collectionsList);
          // set option on stepper
          this.stepperDialogService.setOption(this.initialStepper, this.collectionsList);
        });
    } else {
      this.stepperDialogService.setOption(this.initialStepper, this.collectionsList);
    }
  }

  /**
   * Specific handling for sharing public link. If public link is selected, remove the email field validation because email is optional than.
   * Also change the stepper to show the copy public link step and replace actions if public link is selected.
   * We modify the initialStepper object which is a bit nasty with change detection. So we need to run the change detection manually inside a ngZone.
   *
   * @param event The checkbox change event
   * @param stepperGroup The stepper group name
   */
  private sharingPublicLinkSelection(event: MatCheckboxChange, stepperGroup: string) {
    const swapActions = (stepper: StepperGroupModel) => {
      const actions = stepper.actions;
      stepper.actions = stepper.altActions;
      stepper.altActions = actions;
    };

    const emailFormControl = this.stepperFormGroups.controls[stepperGroup].get('email');
    if (emailFormControl) {
      const emailOptional = this.env.publicSharing?.emailMandatory !== true;
      // update the email properties and set a copy of the formFields to rerender the UI
      if (event.checked) {
        this.ngZone.run(() => {
          if (emailOptional) {
            emailFormControl.removeValidators(Validators.required);
            if (emailFormControl.errors?.['required']) emailFormControl.errors['required'] = false;
            this.initialStepper.stepper[1].formFields = this.initialStepper.stepper[1].formFields?.map((field) => {
              if (field.formFieldName === 'email') {
                delete field.validators;
              }
              return field;
            });
            emailFormControl.updateValueAndValidity();
          }

          this.initialStepper.stepper[2].hidden = false;
          swapActions(this.initialStepper.stepper[1]);
          this.cdr.detectChanges();
        });
      } else {
        this.ngZone.run(() => {
          if (emailOptional) {
            emailFormControl.setValidators([Validators.required]);
            this.initialStepper.stepper[1].formFields = this.initialStepper.stepper[1].formFields?.map((field) => {
              if (field.formFieldName === 'email') {
                field.validators = { required: true };
              }
              return field;
            });
            emailFormControl.updateValueAndValidity();
          }

          this.initialStepper.stepper[2].hidden = true;
          swapActions(this.initialStepper.stepper[1]);
          this.cdr.detectChanges();
        });
      }
    }
  }
}
